import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { propsSenzaPermesso } from "@/shared/auth/permessi";

/** ``HH:MM`` 24h, lo stesso formato che ``<input type="time">`` restituisce. */
const ORA_VALIDA = /^([01]\d|2[0-3]):[0-5]\d$/;

export type AutomaticoPatch = {
  automatico?: boolean;
  ora_automatico?: string;
  ora_automatico_mattina?: string;
};

type Props = {
  possibile: boolean;
  automatico: boolean;
  ora: string;
  /** ``""`` = automatico del mattino spento. */
  oraMattina: string;
  limiteInGiornata: string;
  puo: boolean;
  onCambia: (patch: AutomaticoPatch) => Promise<void>;
};

type CampoProps = {
  etichetta: string;
  valore: string;
  /** Il mattino si spegne svuotando il campo; la sera no. */
  vuotoAmmesso: boolean;
  max?: string;
  busy: boolean;
  disabled: boolean;
  onSalva: (valore: string) => void;
};

/**
 * Un orario dell'automatico. Ha una sua bozza mentre si scrive, e la manda al
 * backend SOLO al blur/Enter, mai a ogni tasto: il campo è controllato dal
 * server, e un PATCH per carattere farebbe combattere la digitazione con
 * l'ultimo giro di rete arrivato (e quell'orario guida prenotazioni che non
 * si annullano).
 */
function CampoOra({ etichetta, valore, vuotoAmmesso, max, busy, disabled, onSalva }: CampoProps) {
  const [bozza, setBozza] = useState(valore);
  const [focus, setFocus] = useState(false);

  // Il valore del server vince SOLO fuori dal focus E fuori dal salvataggio:
  // `salva` toglie il focus SUBITO (prima ancora che il PATCH parta), e senza
  // il controllo su `busy` questo effetto risincronizzava la bozza al vecchio
  // valore mentre «Salvataggio…» era ancora in corso. A salvataggio finito
  // riporta il campo al valore vero: il vecchio se il PATCH è fallito, il
  // nuovo se è riuscito anche con la ricarica fallita (la scheda l'ha già).
  useEffect(() => {
    if (!focus && !busy) setBozza(valore);
  }, [valore, focus, busy]);

  function salva() {
    setFocus(false);
    const valido = ORA_VALIDA.test(bozza) || (vuotoAmmesso && bozza === "");
    if (!valido) {
      setBozza(valore); // orario incompleto/non valido: nessun invio, si torna indietro
      return;
    }
    if (bozza === valore) return; // invariato: niente da salvare
    onSalva(bozza);
  }

  return (
    <label className="flex items-center gap-1.5">
      <span className="font-bold">{etichetta}</span>
      <input
        type="time"
        value={bozza}
        max={max}
        disabled={disabled}
        onFocus={() => setFocus(true)}
        onChange={(e) => setBozza(e.target.value)}
        onBlur={salva}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            salva();
          }
        }}
        className="rounded-full border border-line-2 px-1 tabular-nums text-ink disabled:cursor-not-allowed"
      />
    </label>
  );
}

/** ``HH:MM`` meno un minuto: l'ultimo orario del mattino ancora ammesso. */
function minutoPrima(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(":").map(Number);
  const t = h * 60 + m - 1;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}

/**
 * Riga «Prenota da solo»: due orari (Davide, 05/10). Il mattino, prima del
 * limite delle 11:50, prenota per oggi pomeriggio; la sera per il giorno
 * lavorativo seguente. Un solo `busy` per switch e orari: è lo stesso PATCH,
 * e due richieste in corsa si scavalcherebbero.
 */
export function AutomaticoRiga({
  possibile,
  automatico,
  ora,
  oraMattina,
  limiteInGiornata,
  puo,
  onCambia,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [esito, setEsito] = useState<string | null>(null);
  const senzaPermesso = propsSenzaPermesso(puo, "spedizioni");

  async function cambia(patch: AutomaticoPatch) {
    setBusy(true);
    setEsito(null);
    try {
      await onCambia(patch);
      setEsito("Salvato.");
    } catch (e) {
      setEsito(prettifyApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!possibile) {
    return (
      <div className="flex items-start gap-3 border-t border-line px-5 py-4">
        <p className="text-[12.5px] text-ink">
          Per questo magazzino OM non sa quando la merce è pronta: si prenota col tasto.
        </p>
      </div>
    );
  }

  const campo = { busy, disabled: !puo || busy };
  return (
    <div className="flex items-start gap-3 border-t border-line px-5 py-4">
      <button
        type="button"
        role="switch"
        aria-checked={automatico}
        aria-label="Prenotazione automatica"
        onClick={() => void cambia({ automatico: !automatico })}
        disabled={busy || Boolean(senzaPermesso.disabled)}
        title={senzaPermesso.title}
        aria-busy={busy}
        className={`relative mt-0.5 h-[19px] w-[34px] shrink-0 rounded-full disabled:cursor-not-allowed ${automatico ? "bg-green" : "bg-line-2"}`}
      >
        <span
          className={`absolute top-[2px] h-[15px] w-[15px] rounded-full bg-card transition-all ${automatico ? "right-[2px]" : "left-[2px]"}`}
        />
      </button>
      <div className="text-[12.5px] text-ink">
        <p>
          <span className="font-bold">Prenota da solo</span>
          {busy && <span className="ml-2">Salvataggio…</span>}
          {!busy && esito && (
            <span
              className={`ml-2 ${esito === "Salvato." ? "" : "rounded-sm bg-red-soft px-[6px] py-px font-semibold"}`}
            >
              {esito}
            </span>
          )}
        </p>
        <div className="mt-1.5 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1">
          <CampoOra
            etichetta="Mattino, alle"
            valore={oraMattina}
            vuotoAmmesso
            max={minutoPrima(limiteInGiornata)}
            onSalva={(v) => void cambia({ ora_automatico_mattina: v })}
            {...campo}
          />
          <span>{oraMattina ? "ritiro oggi pomeriggio" : "spento"}</span>
          <CampoOra
            etichetta="Sera, alle"
            valore={ora}
            vuotoAmmesso={false}
            onSalva={(v) => void cambia({ ora_automatico: v })}
            {...campo}
          />
          <span>
            {ora < limiteInGiornata
              ? "ritiro lo stesso giorno"
              : "ritiro il giorno lavorativo seguente"}
          </span>
        </div>
        <p className="mt-1.5">
          Ogni giorno lavorativo, solo se ci sono ordini pronti con LDV. Se oggi c&apos;è già un
          ritiro, il mattino ne aggiunge uno solo per i pronti arrivati dopo. Il mattino si prenota
          entro le {limiteInGiornata}; svuota l&apos;orario per spegnerlo.
        </p>
      </div>
    </div>
  );
}
